import { PaginationState } from 'app/shared/pagination/pagination.model';
import { useMutation, useQuery } from '@tanstack/react-query';
import { showToast } from 'app/shared/util/toast.util';
import { queryClient } from 'app/app';
import { toast } from 'react-toastify';
import { SpecialBuildingPartModel } from 'app/edozvola/cases/special-building-part/model/special-building-part.model';
import {
  deleteSpecialBuildingPart,
  existsSpecialBuildingPart,
  fetchCaseBuildings,
  fetchSpecialBuildingParts,
  getSpecialBuildingPart,
  moveSpecialBuildingPartDown,
  moveSpecialBuildingPartUp,
  saveSpecialBuildingPart,
} from 'app/edozvola/cases/special-building-part/service/special-building-part.service';
import success = toast.success;
import error = toast.error;
import { messages, TABLE_QUERY_KEYS } from 'app/config/constants';

export const fetchSpecialBuildingPartsQuery = (pagination: PaginationState, idCase: string) => {
  return useQuery({
    queryKey: [TABLE_QUERY_KEYS.SPECIAL_BUILDING_PARTS_TABLE, { idCase: idCase, pagination: { ...pagination } }],
    queryFn: ({ signal }) =>
      fetchSpecialBuildingParts(idCase, pagination.page, pagination.size, pagination.column, pagination.direction),
  });
};

export const deleteSpecialBuildingPartQuery = () => {
  return useMutation({
    mutationFn: (idSpecialBuildingPart: number) => deleteSpecialBuildingPart(idSpecialBuildingPart),
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.SPECIAL_BUILDING_PARTS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const existsSpecialBuildingPartMutation = (onResult: (exists: boolean) => void) => {
  return useMutation({
    mutationFn: (key: { idBuilding: number; idBuildingPart: number; idSpecialBuildingPart: number; excludeId: number }) =>
      existsSpecialBuildingPart(key.idBuilding, key.idBuildingPart, key.idSpecialBuildingPart, key.excludeId),
    onSuccess: exists => onResult(exists),
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchSpecialBuildingPartQuery = (idSpecialBuildingPart: string) => {
  return useQuery({
    queryKey: ['special-building-part', idSpecialBuildingPart],
    queryFn: () => getSpecialBuildingPart(idSpecialBuildingPart),
    enabled: !!idSpecialBuildingPart,
  });
};

export const saveSpecialBuildingPartQuery = (idCase: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (specialBuildingPart: SpecialBuildingPartModel) => {
      return saveSpecialBuildingPart(idCase, specialBuildingPart);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.SPECIAL_BUILDING_PARTS_TABLE] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const moveSpecialBuildingPartUpQuery = () => {
  return useMutation({
    mutationFn: (idSpecialBuildingPart: number) => moveSpecialBuildingPartUp(idSpecialBuildingPart),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.SPECIAL_BUILDING_PARTS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const moveSpecialBuildingPartDownQuery = () => {
  return useMutation({
    mutationFn: (idSpecialBuildingPart: number) => moveSpecialBuildingPartDown(idSpecialBuildingPart),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.SPECIAL_BUILDING_PARTS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

/**
 * Zgrade predmeta kao opcije padajuceg izbornika: "vrsta zahvata – namjena".
 */
export const fetchCaseBuildingOptionsQuery = (idCase: string) => {
  return useQuery({
    queryKey: ['special-building-part', 'case-buildings', idCase],
    queryFn: () => fetchCaseBuildings(idCase),
    enabled: !!idCase,
    select: (data: any) =>
      (data?.content ?? []).map(building => ({
        id: building.idBuilding,
        value: building.cbpBuildingPurpose?.split('\n').join(' – '),
        ibz: building.ibz,
      })),
  });
};
